import { TaskListItem } from "../../../api/tasks/taskTypes";
import {
  TableCellType,
  TableHeader,
} from "../../../components/custom-table/table";
import {
  TableCellMultiline,
  TableCellWithBadge,
} from "../../../data-model/tableCells";
import {
  formatCalendarDate,
  formatServiceDate,
} from "../../../utils/dateFormatter";

/** Row-object keys consumed by CustomTable for the Tasks worklist. */
export enum TaskColumnKeys {
  DATE_OF_SERVICE = "dateOfService",
  TASK = "task",
  INBOX_NAME = "inboxName",
  TASK_CATEGORY = "taskCategory",
  PATIENT_DETAILS = "patientDetails",
  DATE_OF_BIRTH = "dateOfBirth",
  PROVIDER_DETAILS = "providerDetails",
  DRUG = "drug",
  KEY = "key",
  OUTCOME = "outcome",
  TASK_STATUS = "taskStatus",
}

const NEUTRAL_PILL = { color: "#2E2E2E", bgColor: "#F5F5F5" };

// Outcome pill colors (text / background) from the design spec.
const OUTCOME_PILL: Record<string, { color: string; bgColor: string }> = {
  pending: { color: "#665D00", bgColor: "#FFFCD6" },
  approved: { color: "#005D49", bgColor: "#E6F3F0" },
  denied: { color: "#CC0300", bgColor: "#FFD6D6" },
  "not started": { color: "#FFFFFF", bgColor: "#404040" },
};

const STATUS_PILL: Record<string, { color: string; bgColor: string }> = {
  complete: { color: "#005D49", bgColor: "#E6F3F0" },
  completed: { color: "#005D49", bgColor: "#E6F3F0" },
  inprogress: { color: "#C24400", bgColor: "#FFF3E0" },
  "in progress": { color: "#C24400", bgColor: "#FFF3E0" },
  open: { color: "#CC0300", bgColor: "#FFE8E8" },
};

/**
 * Build a badge cell, defaulting to a neutral gray pill for unknown values.
 *
 * @param value - Display text (status/outcome/bucket)
 * @param palette - Lookup of lowercased value -> colors
 * @returns Badge cell for CustomTable
 */
const toBadge = (
  value: string | null,
  palette: Record<string, { color: string; bgColor: string }>,
): TableCellWithBadge => {
  const text = value && value.trim() !== "" ? value : "--";
  const colors = palette[text.toLowerCase()] ?? NEUTRAL_PILL;
  return { text, color: colors.color, bgColor: colors.bgColor };
};

export const TasksTableHeader: TableHeader[] = [
  {
    label: "Date of Service",
    key: TaskColumnKeys.DATE_OF_SERVICE,
    order: 0,
    width: 9,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Task",
    key: TaskColumnKeys.TASK,
    order: 1,
    width: 10,
    sortable: false,
    type: TableCellType.STRING_LINK,
  },
  {
    label: "Inbox Name",
    key: TaskColumnKeys.INBOX_NAME,
    order: 2,
    width: 9,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Task Category",
    key: TaskColumnKeys.TASK_CATEGORY,
    order: 3,
    width: 9,
    sortable: true,
    type: TableCellType.BADGE,
  },
  {
    label: "Patient Details",
    key: TaskColumnKeys.PATIENT_DETAILS,
    order: 4,
    width: 12,
    sortable: false,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Date of Birth",
    key: TaskColumnKeys.DATE_OF_BIRTH,
    order: 5,
    width: 9,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Provider Details",
    key: TaskColumnKeys.PROVIDER_DETAILS,
    order: 6,
    width: 10,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Drug",
    key: TaskColumnKeys.DRUG,
    order: 7,
    width: 9,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Key",
    key: TaskColumnKeys.KEY,
    order: 8,
    width: 9,
    sortable: false,
    type: TableCellType.COPY_DATA,
  },
  {
    label: "Outcome",
    key: TaskColumnKeys.OUTCOME,
    order: 9,
    width: 8,
    sortable: true,
    type: TableCellType.BADGE,
  },
  {
    label: "Task Status",
    key: TaskColumnKeys.TASK_STATUS,
    order: 10,
    width: 8,
    sortable: true,
    type: TableCellType.BADGE,
  },
];

/**
 * Map worklist API items to CustomTable row objects.
 *
 * @param items - TaskListItem rows from the worklist API
 * @returns Row objects keyed for CustomTable
 */
export const generateTasksTableData = (
  items: TaskListItem[],
): Record<string, any>[] => {
  return items.map((item) => {
    const patientDetails: TableCellMultiline = {
      mainText: item.patient_name ?? "--",
      secondaryText: `MRN : ${item.patient_mrn ?? "--"}`,
    };
    // created_at is a TIMESTAMPTZ with an explicit offset -> render in local time so
    // it matches the Date-of-Service filter bounds. patient_dob is a plain calendar
    // date -> parse strictly, never through a timezone. Both share dateFormatter.
    const dateOfService = formatServiceDate(item.created_at);
    const dateOfBirth = formatCalendarDate(item.patient_dob);

    return {
      id: item.id,
      [TaskColumnKeys.DATE_OF_SERVICE]: dateOfService,
      [TaskColumnKeys.TASK]: item.title ?? "Task",
      [TaskColumnKeys.INBOX_NAME]: item.inbox_name ?? "--",
      [TaskColumnKeys.TASK_CATEGORY]: toBadge(item.bucket, {}),
      [TaskColumnKeys.PATIENT_DETAILS]: patientDetails,
      [TaskColumnKeys.DATE_OF_BIRTH]: dateOfBirth,
      [TaskColumnKeys.PROVIDER_DETAILS]: item.provider_name ?? "--",
      [TaskColumnKeys.DRUG]: item.drug ?? "--",
      [TaskColumnKeys.KEY]: item.cmm_key ?? "",
      [TaskColumnKeys.OUTCOME]: toBadge(item.outcome_status, OUTCOME_PILL),
      [TaskColumnKeys.TASK_STATUS]: toBadge(item.task_status, STATUS_PILL),
      rowData: JSON.stringify(item),
    };
  });
};
